import { Skeleton, SkeletonCircle } from '@doccareplus/ui';

/**
 * Loading placeholders for the portal home feature. A single `role="status"`
 * region (with an sr-only label) wraps `aria-hidden` shimmer shaped to mirror
 * the real loaded sections — the greeting header, allergy banner, medications
 * list, and recent-visits list — so assistive tech announces "loading" once
 * instead of reading every block.
 */

function LoadingRegion({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="status" aria-label={label}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** A single stacked list row: title line + meta line, matching medication/visit cards. */
function ListRowSkeleton() {
  return (
    <div
      data-skeleton-row
      className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-3.5 shadow-[var(--shadow-1)]"
      aria-hidden
    >
      <div className="flex items-center justify-between gap-2">
        <Skeleton className="h-3.5 w-2/5" />
        <Skeleton className="h-3 w-16" />
      </div>
      <Skeleton className="mt-2 h-3 w-3/5" />
    </div>
  );
}

/** A bordered section: header line plus a few stacked list rows. */
function ListSectionSkeleton({ rows }: { rows: number }) {
  return (
    <div data-skeleton-section className="flex flex-col gap-3" aria-hidden>
      <Skeleton className="h-3.5 w-32" />
      <div className="flex flex-col gap-3">
        {Array.from({ length: rows }).map((_, i) => (
          <ListRowSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * Home page loading state — mirrors the greeting header, hero card, allergy
 * banner, active-medication list, and recent-visits list.
 */
export function HomePageSkeleton({ label }: { label: string }) {
  return (
    <LoadingRegion label={label}>
      <div className="flex flex-col gap-5">
        {/* Greeting header */}
        <div className="flex items-center gap-3 px-0.5" aria-hidden>
          <SkeletonCircle size={40} />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>

        {/* Next-appointment / clinic hero card */}
        <div
          className="overflow-hidden rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-5 shadow-[var(--shadow-1)]"
          aria-hidden
        >
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="mt-2 h-5 w-2/3" />
          <Skeleton className="mt-3 h-9 w-32 rounded-[9px]" />
        </div>

        {/* Allergy banner */}
        <div
          data-skeleton-row
          className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-1)]"
          aria-hidden
        >
          <Skeleton className="h-3.5 w-2/5" />
        </div>

        {/* Active medications list */}
        <ListSectionSkeleton rows={2} />

        {/* Recent visits list */}
        <ListSectionSkeleton rows={2} />
      </div>
    </LoadingRegion>
  );
}
